import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams } from "react-router-dom";
import { syllabusApi } from "../config/syllabusApi";
import "./syllabus.css";

const OG_IMAGE_URL = "https://www.aamschool.in/logo512.png";

const normalizeItems = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    if (Array.isArray(value?.items)) return value.items;
    if (Array.isArray(value?.content)) return value.content;
  }
  return [];
};

export const renderTopics = (topics) => {
  if (Array.isArray(topics)) return topics.map((topic) => `ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â¢ ${topic}`);
  if (topics && typeof topics === "object") {
    return Object.entries(topics).map(([key, value]) => ({
      section: key,
      items: Array.isArray(value) ? value : [String(value)],
    }));
  }
  return [];
};

const formatDate = (value) => {
  const text = String(value || "").trim();
  return text || "NA";
};

const pickFirstValue = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (value != null && value !== "") return value;
  }
  return "";
};

const normalizeSyllabusRecord = (value) => {
  const base = value && typeof value === "object" ? value : {};
  const content =
    base?.content && typeof base.content === "object" && !Array.isArray(base.content)
      ? base.content
      : {};
  const syllabus =
    base?.syllabus && typeof base.syllabus === "object" && !Array.isArray(base.syllabus)
      ? base.syllabus
      : {};

  return {
    ...base,
    ...content,
    ...syllabus,
    examName: pickFirstValue(
      syllabus?.examName,
      content?.examName,
      base?.examName,
      syllabus?.exam_name,
      content?.exam_name,
      base?.exam_name,
      syllabus?.title,
      content?.title,
      base?.title,
      syllabus?.name,
      content?.name,
      base?.name
    ),
    examCode: pickFirstValue(
      syllabus?.examCode,
      content?.examCode,
      base?.examCode,
      syllabus?.examKey,
      content?.examKey,
      base?.examKey,
      syllabus?.exam_key,
      content?.exam_key,
      base?.exam_key,
      syllabus?.exam_code,
      content?.exam_code,
      base?.exam_code,
      syllabus?.code,
      content?.code,
      base?.code,
      syllabus?.examId,
      content?.examId,
      base?.examId
    ),
    examKey: pickFirstValue(
      syllabus?.examKey,
      content?.examKey,
      base?.examKey,
      syllabus?.exam_key,
      content?.exam_key,
      base?.exam_key,
      syllabus?.examCode,
      content?.examCode,
      base?.examCode,
      syllabus?.exam_code,
      content?.exam_code,
      base?.exam_code
    ),
    updatedAt: pickFirstValue(
      base?.updatedAt,
      syllabus?.updatedAt,
      content?.updatedAt,
      base?.updated_at,
      syllabus?.updated_at,
      content?.updated_at,
      base?.date,
      syllabus?.date,
      content?.date,
      base?.createdAt,
      syllabus?.createdAt,
      content?.createdAt
    ),
    slugurl: pickFirstValue(
      base?.slugurl,
      content?.slugurl,
      syllabus?.slugurl,
      base?.slugWithId,
      content?.slugWithId,
      syllabus?.slugWithId
    ),
    slugWithId: pickFirstValue(
      base?.slugWithId,
      content?.slugWithId,
      syllabus?.slugWithId,
      base?.slugurl,
      content?.slugurl,
      syllabus?.slugurl
    ),
  };
};
const buildExamKeyPath = (examKey = "") => `/syllabus/exam/${encodeURIComponent(examKey)}`;
const buildBlueprintPath = (examKey = "") =>
  `/syllabus/exam/${encodeURIComponent(examKey)}/test-series-blueprint`;

const normalizeBlueprintRows = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.papers)) return value.papers;
  if (Array.isArray(value?.subjects)) return value.subjects;
  if (Array.isArray(value?.blueprint)) return value.blueprint;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const normalizeTopicCountEntries = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  return Object.entries(value);
};

const formatLabel = (key) =>
  String(key || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const renderPrimitive = (value) => {
  if (value == null || value === "") return "NA";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const isPrimitiveLike = (value) =>
  value == null || ["string", "number", "boolean"].includes(typeof value);

const toInlineValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => renderPrimitive(item)).join(", ");
  }
  return renderPrimitive(value);
};

const canRenderInlineObject = (entries = []) =>
  entries.every(([, value]) =>
    isPrimitiveLike(value) ||
    (Array.isArray(value) && value.every((item) => isPrimitiveLike(item)))
  );

function KeyValueLine({ label, value }) {
  if (value == null || value === "") return null;

  return (
    <p className="syllabus-key-line">
      <strong>{`${label} : `}</strong>
      <span>{renderPrimitive(value)}</span>
    </p>
  );
}

function InlineMetaLine({ entries = [] }) {
  const visibleEntries = entries.filter((item) => item?.value != null && item.value !== "");
  if (!visibleEntries.length) return null;

  return (
    <p className="syllabus-inline-meta-line">
      {visibleEntries.map((item, index) => (
        <React.Fragment key={item.label}>
          {index > 0 ? <span className="syllabus-inline-separator"> | </span> : null}
          <strong>{`${item.label}: `}</strong>
          <span>{renderPrimitive(item.value)}</span>
        </React.Fragment>
      ))}
    </p>
  );
}

function ExamPatternBlock({ value }) {
  const base = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const qualifyingMarks =
    base?.qualifyingMarks && typeof base.qualifyingMarks === "object" && !Array.isArray(base.qualifyingMarks)
      ? base.qualifyingMarks
      : base?.qualifying_marks && typeof base.qualifying_marks === "object" && !Array.isArray(base.qualifying_marks)
        ? base.qualifying_marks
        : {};

  const primitiveEntries = Object.entries(base).filter(([key, entryValue]) => {
    if (["qualifyingMarks", "qualifying_marks"].includes(key)) return false;
    if (entryValue == null) return false;
    if (typeof entryValue === "string") return entryValue.trim() !== "";
    if (Array.isArray(entryValue)) return entryValue.length > 0;
    if (typeof entryValue === "object") return Object.keys(entryValue).length > 0;
    return true;
  });

  const qualifyingEntries = Object.entries(qualifyingMarks).filter(([, entryValue]) => {
    if (entryValue == null) return false;
    return String(entryValue).trim() !== "";
  });

  return (
    <div className="syllabus-exam-pattern-block">
      {primitiveEntries.length ? (
        <div className="syllabus-inline-stack">
          {primitiveEntries.map(([entryKey, entryValue]) => (
            <KeyValueLine key={entryKey} label={formatLabel(entryKey)} value={toInlineValue(entryValue)} />
          ))}
        </div>
      ) : null}
      {qualifyingEntries.length ? (
        <div className="syllabus-exam-pattern-extra">
          <h4>Qualifying Marks</h4>
          <div className="syllabus-inline-stack">
            {qualifyingEntries.map(([entryKey, entryValue]) => (
              <KeyValueLine key={entryKey} label={formatLabel(entryKey)} value={renderPrimitive(entryValue)} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
function SubjectsList({ subjects }) {
  if (!Array.isArray(subjects) || !subjects.length) {
    return <p className="syllabus-empty-state">No subjects available.</p>;
  }

  return (
    <div className="syllabus-subject-list">
      {subjects.map((subject, subjectIndex) => {
        const subjectName =
          subject?.subjectName ||
          subject?.name ||
          subject?.subject ||
          subject?.title ||
          `Subject ${subjectIndex + 1}`;
        const renderedTopics = renderTopics(subject?.topics);

        let topicText = "NA";
        if (renderedTopics.length) {
          if (typeof renderedTopics[0] === "string") {
            topicText = renderedTopics
              .map((item) => String(item).replace(/^ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â¢\s*/, ""))
              .join(", ");
          } else {
            topicText = renderedTopics
              .map((group) => `${formatLabel(group.section)}: ${group.items.map((item) => renderPrimitive(item)).join(", ")}`)
              .join(" | ");
          }
        }

        return (
          <div className="syllabus-subject-item" key={`${subjectName}-${subjectIndex}`}>
            <p className="syllabus-subject-title-row">
              <strong>{subjectName}</strong>
              {(subject?.questions != null || subject?.marks != null) ? (
                <span className="syllabus-subject-title-meta">
                  {subject?.questions != null ? `Questions: ${renderPrimitive(subject.questions)}` : ""}
                  {subject?.questions != null && subject?.marks != null ? ", " : ""}
                  {subject?.marks != null ? `Marks: ${renderPrimitive(subject.marks)}` : ""}
                </span>
              ) : null}
            </p>
            <p className="syllabus-copy">{topicText}</p>
            <div className="syllabus-subject-meta">
              <InlineMetaLine
                entries={[
                  { label: "Applicable For", value: subject?.applicableFor },
                  { label: "Notes", value: subject?.notes },
                  {
                    label: "Options",
                    value: Array.isArray(subject?.options) && subject.options.length
                      ? subject.options.join(", ")
                      : "",
                  },
                ]}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PapersList({ papers }) {
  if (!Array.isArray(papers) || !papers.length) {
    return <p className="syllabus-empty-state">No papers available.</p>;
  }

  return (
    <div className="syllabus-paper-list">
      {papers.map((paper, index) => {
        const paperCode = paper?.paper_code || paper?.paperCode || `PAPER_${index + 1}`;
        const paperName = paper?.paper_name || paper?.paperName || `Paper ${index + 1}`;

        return (
          <div className="syllabus-paper-item" key={`${paperCode}-${index}`}>
            <p className="syllabus-paper-title-row">
              <strong>Papers:</strong> <span>{`Papers ${index + 1}`}</span>
              <span className="syllabus-inline-separator">|</span>
              <strong>Paper Code:</strong> <span>{paperCode}</span>
              <span className="syllabus-inline-separator">|</span>
              <strong>Paper Name:</strong> <span>{paperName}</span>
            </p>
            <InlineMetaLine
              entries={[
                { label: "Applicable For", value: paper?.applicableFor },
              ]}
            />
            {paper?.subjects ? (
              <div className="syllabus-paper-subjects">
                <h4>Subjects</h4>
                <SubjectsList subjects={paper.subjects} />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
const renderSyllabusValue = (key, value, depth = 0) => {
  if (key === "papers" && Array.isArray(value)) {
    return <PapersList papers={value} />;
  }

  if (key === "subjects" && Array.isArray(value)) {
    return <SubjectsList subjects={value} />;
  }

  if (key === "exam_pattern" || key === "examPattern") {
    return <ExamPatternBlock value={value} />;
  }

  if (key === "topics") {
    const renderedTopics = renderTopics(value);
    if (!renderedTopics.length) return <p className="syllabus-empty-state">No topics listed.</p>;

    if (typeof renderedTopics[0] === "string") {
      return (
        <ul className="syllabus-topic-list">
          {renderedTopics.map((topic, index) => (
            <li key={`topic-${index}`}>{String(topic).replace(/^ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â¢\s*/, "")}</li>
          ))}
        </ul>
      );
    }

    return (
      <div className="syllabus-topic-stack">
        {renderedTopics.map((group) => (
          <div className="syllabus-topic-group" key={group.section}>
            <h4>{formatLabel(group.section)}</h4>
            <ul className="syllabus-topic-list">
              {group.items.map((item, index) => (
                <li key={`${group.section}-${index}`}>{renderPrimitive(item)}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  }

  if (Array.isArray(value)) {
    if (!value.length) return <p className="syllabus-empty-state">No items available.</p>;

    if (value.every((item) => item == null || ["string", "number", "boolean"].includes(typeof item))) {
      return (
        <ul className="syllabus-nested-list">
          {value.map((item, index) => (
            <li key={`${key}-${index}`}>{renderPrimitive(item)}</li>
          ))}
        </ul>
      );
    }

    return (
      <div className="syllabus-array-stack">
        {value.map((item, index) => (
          <div
            className={`syllabus-section syllabus-section-nested depth-${Math.min(depth + 1, 3)}`}
            key={`${key}-${index}`}
          >
            <h3>{`${formatLabel(key)} ${index + 1}`}</h3>
            {renderSyllabusObject(item, depth + 1)}
          </div>
        ))}
      </div>
    );
  }

  if (value && typeof value === "object") {
    return renderSyllabusObject(value, depth + 1);
  }

  return <p className="syllabus-copy">{renderPrimitive(value)}</p>;
};

const renderSyllabusObject = (objectValue, depth = 0) => {
  if (!objectValue || typeof objectValue !== "object") {
    return <p className="syllabus-copy">{renderPrimitive(objectValue)}</p>;
  }

  const entries = Object.entries(objectValue).filter(([, value]) => {
    if (value == null) return false;
    if (typeof value === "string") return value.trim() !== "";
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "object") return Object.keys(value).length > 0;
    return true;
  });

  if (!entries.length) {
    return <p className="syllabus-empty-state">No details available.</p>;
  }

  if (canRenderInlineObject(entries)) {
    return (
      <div className="syllabus-inline-stack">
        {entries.map(([key, value]) => (
          <KeyValueLine key={key} label={formatLabel(key)} value={toInlineValue(value)} />
        ))}
      </div>
    );
  }

  return (
    <div className={`syllabus-content-stack depth-${Math.min(depth, 3)}`}>
      {entries.map(([key, value]) => (
        <section
          className={`syllabus-section ${depth > 0 ? `syllabus-section-nested depth-${Math.min(depth, 3)}` : "syllabus-section-root"}`}
          key={key}
        >
          <h3>{formatLabel(key)}</h3>
          {renderSyllabusValue(key, value, depth)}
        </section>
      ))}
    </div>
  );
};

const SyllabusListItems = ({ items = [] }) => (
  <div className="syllabus-list">
    {items.map((item, index) => {
      const slug = item?.slugurl || item?.slugWithId || item?.slug || "";
      return (
        <article className="syllabus-list-item" key={`${slug || item?.id || "syllabus"}-${index}`}>
          <div>
            <h3>
              <Link to={`/syllabus/${slug}`}>{item?.examName || "Untitled Syllabus"}</Link>
            </h3>
            <div className="syllabus-meta">
              <span className="syllabus-meta-chip">Code: {item?.examCode || "NA"}</span>
              <span className="syllabus-meta-chip">Updated: {formatDate(item?.updatedAt)}</span>
            </div>
          </div>
          <Link className="syllabus-view-link" to={`/syllabus/${slug}`}>
            View Syllabus
          </Link>
        </article>
      );
    })}
  </div>
);

export function SyllabusHomeSection({ searchTerm = "" }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const data = await syllabusApi.latest();
        if (!mounted) return;
        setItems(normalizeItems(data));
      } catch (err) {
        if (!mounted) return;
        setError(err.message || "Unable to load syllabus.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const filteredItems = useMemo(() => {
    const query = String(searchTerm || "").trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) =>
      `${item?.examName || ""} ${item?.examCode || ""}`.toLowerCase().includes(query)
    );
  }, [items, searchTerm]);

  return (
    <div className="card">
      <h3 className="categoryheader">Syllabus</h3>
      {loading ? <p>Loading...</p> : null}
      {error ? <p className="syllabus-error">{error}</p> : null}
      {!loading && !error ? (
        <>
          <ul id="jobsList" className="syllabus-home-list">
            {filteredItems.slice(0, 20).map((item, index) => {
              const slug = item?.slugurl || item?.slugWithId || item?.slug || "";
              return (
                <li key={`${slug || item?.id || "syllabus"}-${index}`}>
                  <Link to={`/syllabus/${slug}`}>{item?.examName || "Untitled Syllabus"}</Link>
                </li>
              );
            })}
          </ul>
          {filteredItems.length > 20 ? (
            <button className="view-more-btn" onClick={() => navigate("/syllabus")}>
              View More
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

export function SyllabusListPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const data = await syllabusApi.latest();
        if (!mounted) return;
        setItems(normalizeItems(data));
      } catch (err) {
        if (!mounted) return;
        setError(err.message || "Unable to load syllabus.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="syllabus-page">
      <Helmet>
        <title>Exam Syllabus | AamSchool</title>
        <meta name="description" content="Browse the latest syllabus updates by exam name and exam code." />
        <meta property="og:title" content="Exam Syllabus | AamSchool" />
        <meta property="og:description" content="Browse the latest syllabus updates by exam name and exam code." />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:url" content="https://www.aamschool.in/syllabus" />
        <meta property="og:type" content="website" />
      </Helmet>
      <h1>Exam Syllabus</h1>
      <p className="syllabus-page-copy">
        Browse the latest syllabus updates by exam name and exam code.
      </p>
      <div className="syllabus-list-shell">
        {loading ? <p>Loading syllabus...</p> : null}
        {error ? <p className="syllabus-error">{error}</p> : null}
        {!loading && !error && !items.length ? (
          <p className="syllabus-empty-state">No syllabus available right now.</p>
        ) : null}
        {!loading && !error && items.length ? <SyllabusListItems items={items} /> : null}
      </div>
    </div>
  );
}

export function SyllabusDetailPage() {
  const { slugWithId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const response = await syllabusApi.getBySlug(slugWithId);
        if (!mounted) return;
        setData(response);
      } catch (err) {
        if (!mounted) return;
        if (Number(err?.status) === 404) {
          setError("Syllabus not found");
          return;
        }
        setError(err.message || "Unable to load syllabus.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [slugWithId]);

  const normalizedData = useMemo(() => normalizeSyllabusRecord(data), [data]);
  const title = normalizedData?.examName || "Syllabus";
  const canonicalUrl = `https://www.aamschool.in/syllabus/${slugWithId || ""}`;
  const examCode = normalizedData?.examCode || "NA";
  const examKey = normalizedData?.examKey || "";
  const updatedAt = formatDate(normalizedData?.updatedAt);
  const detailObject = useMemo(() => {
    const baseDetailObject = Object.fromEntries(
      Object.entries(normalizedData || {}).filter(
        ([key]) =>
          ![
            "id",
            "content",
            "syllabus",
            "createdAt",
            "created_at",
            "examName",
            "exam_name",
            "title",
            "name",
            "examCode",
            "examKey",
            "exam_key",
            "exam_code",
            "code",
            "examId",
            "updatedAt",
            "updated_at",
            "date",
            "slugurl",
            "slugWithId",
            "conductingBody",
            "conducting_body",
            "notificationYear",
            "notification_year",
          ].includes(key)
      )
    );

    const conductingBody = pickFirstValue(
      normalizedData?.conductingBody,
      normalizedData?.conducting_body
    );
    const notificationYear = pickFirstValue(
      normalizedData?.notificationYear,
      normalizedData?.notification_year
    );
    const combinedAuthority = [
      conductingBody,
      notificationYear ? `Notification Year: ${notificationYear}` : "",
    ]
      .filter(Boolean)
      .join(" | ");

    return combinedAuthority
      ? {
          conducting_body_and_notification_year: combinedAuthority,
          ...baseDetailObject,
        }
      : baseDetailObject;
  }, [normalizedData]);
  return (
    <div className="syllabus-page syllabus-page-detail">
      <Helmet>
        <title>{`${title} | AamSchool`}</title>
        <meta name="description" content={`${title} syllabus details on AamSchool.`} />
        <meta property="og:title" content={`${title} | AamSchool`} />
        <meta property="og:description" content={`${title} syllabus details on AamSchool.`} />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
      </Helmet>
      {loading ? <p>Loading syllabus...</p> : null}
      {!loading && error ? <p className="syllabus-error">{error}</p> : null}
      {!loading && !error && data ? (
        <div className="syllabus-detail-shell">
          <div className="syllabus-detail-topbar">
            <div className="syllabus-actions syllabus-detail-actions">
              {examKey ? (
                <Link className="syllabus-view-link" to={buildBlueprintPath(examKey)}>
                  Test Series Blueprint
                </Link>
              ) : null}
              <Link className="syllabus-view-link" to="/syllabus">
                Back to syllabus list
              </Link>
            </div>
          </div>
          <header className="syllabus-detail-page-heading syllabus-detail-hero-block">
            <p className="syllabus-detail-eyebrow">Exam Syllabus</p>
            <h1>{title}</h1>
            <p className="syllabus-detail-meta-line">
              <span><strong>Exam Code</strong>{examCode}</span>
              <span><strong>Exam Key</strong>{examKey || "NA"}</span>
              <span><strong>Updated</strong>{updatedAt}</span>
            </p>
            <p className="syllabus-page-copy syllabus-detail-summary">
              Read the latest syllabus structure, paper pattern, subject coverage, and topic breakdown in one place.
            </p>
          </header>
          <div className="syllabus-detail-content">{renderSyllabusObject(detailObject)}</div>
        </div>
      ) : null}
    </div>
  );
}
function BlueprintRowCard({ row, index }) {
  const paperName =
    row?.paperName || row?.paper || row?.subjectName || row?.subject || `Section ${index + 1}`;
  const normalizedTopics = Array.isArray(row?.normalizedTopics) ? row.normalizedTopics : [];
  const topicCounts = normalizeTopicCountEntries(row?.availableQuestionCountByTopic);

  return (
    <article className="syllabus-paper-item">
      <KeyValueLine label="Paper / Subject" value={paperName} />
      <InlineMetaLine
        entries={[
          { label: "Available Questions", value: row?.availableQuestionsForSubject },
          { label: "Questions Per Series", value: row?.questionsPerSeries },
          {
            label: "Exam Mapping",
            value:
              row?.matchedQuestionExamId == null
                ? "Exam mapping not found in question bank"
                : row?.matchedQuestionExamId,
          },
        ]}
      />

      <div className="syllabus-paper-subjects">
        <h4>Normalized Topics</h4>
        {normalizedTopics.length ? (
          <ul className="syllabus-topic-list">
            {normalizedTopics.map((topic, topicIndex) => (
              <li key={`${paperName}-topic-${topicIndex}`}>{renderPrimitive(topic)}</li>
            ))}
          </ul>
        ) : (
          <p className="syllabus-empty-state">No normalized topics available.</p>
        )}
      </div>

      <div className="syllabus-paper-subjects">
        <h4>Available Question Count By Topic</h4>
        {topicCounts.length ? (
          <div className="syllabus-table-wrap">
            <table className="syllabus-mini-table">
              <thead>
                <tr>
                  <th>Topic</th>
                  <th>Available Questions</th>
                </tr>
              </thead>
              <tbody>
                {topicCounts.map(([topic, count]) => (
                  <tr key={`${paperName}-${topic}`}>
                    <td>{topic}</td>
                    <td>{renderPrimitive(count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="syllabus-empty-state">No topic-level question counts available.</p>
        )}
      </div>
    </article>
  );
}

export function SyllabusExamKeyPage() {
  const { examKey } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const response = await syllabusApi.getByExamKey(examKey);
        if (!mounted) return;
        setData(response);
      } catch (err) {
        if (!mounted) return;
        setError(Number(err?.status) === 404 ? "Syllabus not found" : err.message || "Unable to load syllabus.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [examKey]);

  const normalizedData = useMemo(() => normalizeSyllabusRecord(data), [data]);
  const title = normalizedData?.examName || "Syllabus";

  return (
    <div className="syllabus-page syllabus-page-detail">
      <Helmet>
        <title>{`${title} | AamSchool`}</title>
        <meta
          name="description"
          content={`Read the syllabus and test-series blueprint overview for ${title}.`}
        />
        <meta property="og:title" content={`${title} | AamSchool`} />
        <meta
          property="og:description"
          content={`Read the syllabus and test-series blueprint overview for ${title}.`}
        />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:url" content={`https://www.aamschool.in${buildExamKeyPath(examKey || "")}`} />
        <meta property="og:type" content="article" />
      </Helmet>
      {loading ? <p>Loading syllabus...</p> : null}
      {!loading && error ? <p className="syllabus-error">{error}</p> : null}
      {!loading && !error && data ? (
        <div className="syllabus-detail-shell">
          <div className="syllabus-detail-page-heading">
            <p className="syllabus-detail-eyebrow">Exam Syllabus</p>
            <h1>{title}</h1>
          </div>
          <div className="syllabus-actions syllabus-detail-actions">
            <Link className="syllabus-view-link" to={buildBlueprintPath(examKey || "")}>
              Test Series Blueprint
            </Link>
            <Link className="syllabus-view-link" to="/syllabus">
              Back to syllabus list
            </Link>
          </div>
          <div className="syllabus-detail-content">{renderSyllabusObject(normalizedData)}</div>
        </div>
      ) : null}
    </div>
  );
}

export function SyllabusBlueprintPage() {
  const { examKey } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const response = await syllabusApi.getTestSeriesBlueprint(examKey);
        if (!mounted) return;
        setData(response);
      } catch (err) {
        if (!mounted) return;
        setError(Number(err?.status) === 404 ? "Blueprint not found" : err.message || "Unable to load blueprint.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [examKey]);

  const blueprintRows = useMemo(() => normalizeBlueprintRows(data), [data]);
  const title =
    data?.examName || data?.title || decodeURIComponent(String(examKey || "")).replace(/[-_]+/g, " ");

  return (
    <div className="syllabus-page">
      <Helmet>
        <title>{`${title} Test Series Blueprint | AamSchool`}</title>
        <meta
          name="description"
          content={`Review the test series blueprint and question-bank coverage for ${title}.`}
        />
        <meta property="og:title" content={`${title} Test Series Blueprint | AamSchool`} />
        <meta
          property="og:description"
          content={`Review the test series blueprint and question-bank coverage for ${title}.`}
        />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:url" content={`https://www.aamschool.in${buildBlueprintPath(examKey || "")}`} />
        <meta property="og:type" content="article" />
      </Helmet>

      <div className="syllabus-header-card">
        <div>
          <h1>{title} Test Series Blueprint</h1>
          <p className="syllabus-page-copy">
            This blueprint shows paper and subject coverage available from the question bank.
          </p>
        </div>
        <div className="syllabus-actions">
          <Link className="syllabus-view-link" to={buildExamKeyPath(examKey || "")}>
            View Syllabus
          </Link>
          <Link className="syllabus-view-link" to="/syllabus">
            Back to syllabus list
          </Link>
        </div>
      </div>

      {loading ? <p>Loading blueprint...</p> : null}
      {error ? <p className="syllabus-error">{error}</p> : null}
      {!loading && !error && !blueprintRows.length ? (
        <p className="syllabus-empty-state">No blueprint data available.</p>
      ) : null}
      {!loading && !error && blueprintRows.length ? (
        <div className="syllabus-paper-list">
          {blueprintRows.map((row, index) => (
            <BlueprintRowCard
              key={`${row?.paperName || row?.subjectName || "blueprint"}-${index}`}
              row={row}
              index={index}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

















